import { useState, useEffect } from "react";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Check, X, Menu, ChevronDown, ChevronUp, Zap, TrendingUp, Building2, Globe, Shield, Lock, RefreshCw, Phone } from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

// ─── Design tokens ──────────────────────────────────────────────────────────

const BLUE = "#3B82F6";
const INDIGO = "#6366f1";
const PURPLE = "#8b5cf6";
const DARK_BG = "#05070a";
const CARD_BG = "rgba(255,255,255,0.018)";
const CARD_BORDER = "rgba(255,255,255,0.08)";
const MUTED = "#94a3b8";
const DIMMED = "#475569";
const GREEN = "#22c55e";
const AMBER = "#f59e0b";
const RED = "#ef4444";

// ─── Tier definitions ────────────────────────────────────────────────────────

interface Tier {
  id: string;
  name: string;
  displayName: string;
  subtitle: string;
  icon: React.ReactNode;
  priceMonthly: number | null;
  priceAnnual: number | null;
  billingNote: string;
  badgeLabel?: string;
  badgeColour?: string;
  features: string[];
  restrictionNote?: string;
  cta: string;
  ctaHref?: string;
  ctaAction?: "checkout";
  planKey?: string;
  ctaStyle: "outline-white" | "outline-blue" | "filled-blue" | "filled-indigo" | "outline-dark";
  enterprise?: boolean;
  elevated?: boolean;
}

const TIERS: Tier[] = [
  {
    id: "free",
    name: "free",
    displayName: "Try Vexis",
    subtitle: "Experience the full Vexis pipeline, free forever.",
    icon: <Zap className="w-4 h-4" />,
    priceMonthly: 0,
    priceAnnual: 0,
    billingNote: "No credit card required",
    features: [
      "1 team member seat",
      "1 active role at a time",
      "3 candidates maximum",
      "30-day role expiry",
      "Full video assessment experience",
      "AI scoring and transcription",
      "Human review gate",
      "Basic candidate dossier",
      "JobAdder integration",
    ],
    restrictionNote: "⚠ Role expires after 30 days. Upgrade to keep active roles open longer and assess more candidates.",
    cta: "Get Started Free",
    ctaHref: "/register",
    ctaStyle: "outline-white",
  },
  {
    id: "starter",
    name: "starter",
    displayName: "Starter",
    subtitle: "For individual recruiters growing their placement volume.",
    icon: <TrendingUp className="w-4 h-4" />,
    priceMonthly: 149,
    priceAnnual: 119,
    billingNote: "Billed monthly or annually",
    features: [
      "Up to 3 team members",
      "Everything in Free",
      "3 simultaneous active roles",
      "25 candidates per month",
      "Roles stay open until filled",
      "Full AI scoring with confidence bands",
      "Structured candidate dossier",
      "JobAdder write-backs",
      "Email + SMS candidate notifications",
      "Role-specific question builder",
      "Standard support",
    ],
    cta: "Get Started",
    ctaAction: "checkout",
    planKey: "starter",
    // NOTE: Stripe price IDs for starter annual/monthly are configured via
    // STRIPE_STARTER_PRICE_ID and STRIPE_STARTER_ANNUAL_PRICE_ID env vars
    ctaStyle: "outline-blue",
  },
  {
    id: "growth",
    name: "growth",
    displayName: "Growth",
    subtitle: "For teams running multiple active hiring campaigns.",
    icon: <Building2 className="w-4 h-4" />,
    priceMonthly: 299,
    priceAnnual: 239,
    billingNote: "Billed monthly or annually",
    badgeLabel: "Most Popular",
    badgeColour: BLUE,
    elevated: true,
    features: [
      "Up to 8 team members",
      "Everything in Starter",
      "10 simultaneous active roles",
      "100 candidates per month",
      "Remove Vexis branding option",
      "Custom email templates",
      "Priority AI processing",
      "Candidate assessment and review tools",
      "Dossier sharing with clients",
      "Advanced analytics dashboard",
      "Priority email support",
    ],
    cta: "Get Started",
    ctaAction: "checkout",
    planKey: "growth",
    // NOTE: Stripe price IDs for growth annual/monthly are configured via
    // STRIPE_PROFESSIONAL_PRICE_ID and STRIPE_GROWTH_ANNUAL_PRICE_ID env vars
    ctaStyle: "filled-blue",
  },
  {
    id: "agency",
    name: "agency",
    displayName: "Agency",
    subtitle: "For high-volume agencies managing multiple clients.",
    icon: <Globe className="w-4 h-4" />,
    priceMonthly: 549,
    priceAnnual: 439,
    billingNote: "Billed monthly or annually",
    badgeLabel: "Best for Agencies",
    badgeColour: PURPLE,
    features: [
      "Up to 15 team members",
      "Everything in Growth",
      "Unlimited active roles",
      "300 candidates per month",
      "Full white label — your brand throughout",
      "Custom subdomain support",
      "Client portal for dossier delivery",
      "Bulk candidate importing",
      "Advanced reporting and exports",
      "JobAdder multi-workspace support",
      "Dedicated account manager",
      "Phone and priority support",
    ],
    cta: "Get Started",
    ctaAction: "checkout",
    planKey: "agency",
    // NOTE: Stripe price IDs for agency annual/monthly are configured via
    // STRIPE_AGENCY_PRICE_ID and STRIPE_AGENCY_ANNUAL_PRICE_ID env vars
    ctaStyle: "filled-indigo",
  },
  {
    id: "enterprise",
    name: "enterprise",
    displayName: "Enterprise",
    subtitle: "For large agencies and enterprise talent teams.",
    icon: <Shield className="w-4 h-4" />,
    priceMonthly: null,
    priceAnnual: null,
    billingNote: "Annual contract with invoicing",
    features: [
      "Unlimited team members",
      "Everything in Agency",
      "Unlimited candidates",
      "Custom ATS integrations",
      "SSO / SAML authentication",
      "SLA-backed uptime guarantee",
      "Custom AI scoring criteria",
      "Dedicated infrastructure",
      "Executive onboarding support",
    ],
    cta: "Talk to Us",
    ctaHref: "mailto:partnerships@vexistalentagency.com",
    ctaStyle: "outline-dark",
    enterprise: true,
  },
];

// ─── Comparison table data ───────────────────────────────────────────────────

interface FeatureRow {
  label: string;
  free: string | boolean;
  starter: string | boolean;
  growth: string | boolean;
  agency: string | boolean;
  enterprise: string | boolean;
}

const COMPARISON_ROWS: FeatureRow[] = [
  { label: "Active roles",           free: "1",       starter: "3",        growth: "10",        agency: "Unlimited",  enterprise: "Unlimited" },
  { label: "Candidates / month",     free: "3",       starter: "25",       growth: "100",       agency: "300",        enterprise: "Unlimited" },
  { label: "Team seats",             free: "1",       starter: "3",        growth: "8",         agency: "15",         enterprise: "Unlimited" },
  { label: "Role expiry",            free: "30 days", starter: "None",     growth: "None",      agency: "None",       enterprise: "None" },
  { label: "Video assessments",      free: true,      starter: true,       growth: true,        agency: true,         enterprise: true },
  { label: "AI scoring",             free: true,      starter: true,       growth: true,        agency: true,         enterprise: true },
  { label: "Human review gate",      free: true,      starter: true,       growth: true,        agency: true,         enterprise: true },
  { label: "Candidate dossier",      free: "Basic",   starter: "Full",     growth: "Full",      agency: "Full",       enterprise: "Custom" },
  { label: "JobAdder integration",   free: true,      starter: true,       growth: true,        agency: true,         enterprise: true },
  { label: "Remove Vexis branding",  free: false,     starter: false,      growth: true,        agency: true,         enterprise: true },
  { label: "Custom email templates", free: false,     starter: false,      growth: true,        agency: true,         enterprise: true },
  { label: "White label",            free: false,     starter: false,      growth: false,       agency: true,         enterprise: true },
  { label: "Client portal",          free: false,     starter: false,      growth: false,       agency: true,         enterprise: true },
  { label: "Dedicated support",      free: false,     starter: "Email",    growth: "Priority",  agency: "Account mgr", enterprise: "Executive" },
];

// ─── FAQ data ────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: "How do I try Vexis before committing to a paid plan?",
    a: "Start on the Try Vexis free plan — no credit card required. You get the full assessment experience with 1 active role and 3 candidates. When you're ready to scale up, upgrade to any paid plan at any time.",
  },
  {
    q: "What happens when my free role expires?",
    a: "Your free role closes automatically after 30 days. Any candidates already in your pipeline remain accessible. To open a new role, simply upgrade to a paid plan.",
  },
  {
    q: "Do I pay per interview or per candidate?",
    a: "No. Vexis uses flat monthly pricing — there are no per-interview or per-candidate charges. Your monthly fee covers everything within your plan limits.",
  },
  {
    q: "Can I change plans at any time?",
    a: "Yes. Upgrade or downgrade at any time. Upgrades take effect immediately. Downgrades take effect at the end of your current billing period.",
  },
  {
    q: "Is the JobAdder integration included in all plans?",
    a: "Yes. JobAdder integration is included on every plan including the free tier. Connect your JobAdder account and start sending assessments from within JobAdder immediately.",
  },
];

// ─── Helper: CTA button style resolver ──────────────────────────────────────

function ctaButtonStyle(style: Tier["ctaStyle"], isCurrentPlan: boolean): React.CSSProperties {
  if (isCurrentPlan) {
    return {
      width: "100%", background: "transparent", border: `1px solid ${GREEN}`,
      color: GREEN, fontSize: 13, fontWeight: 600, cursor: "default",
    };
  }
  switch (style) {
    case "outline-white":
      return { width: "100%", background: "transparent", border: "1px solid rgba(255,255,255,0.35)", color: "#f1f5f9", fontSize: 13, fontWeight: 600 };
    case "outline-blue":
      return { width: "100%", background: "transparent", border: `1px solid ${BLUE}`, color: BLUE, fontSize: 13, fontWeight: 600 };
    case "filled-blue":
      return { width: "100%", background: BLUE, border: "none", color: "#fff", fontSize: 13, fontWeight: 700 };
    case "filled-indigo":
      return { width: "100%", background: PURPLE, border: "none", color: "#fff", fontSize: 13, fontWeight: 700 };
    case "outline-dark":
      return { width: "100%", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: MUTED, fontSize: 13, fontWeight: 600 };
    default:
      return { width: "100%", fontSize: 13, fontWeight: 600 };
  }
}

// ─── Helper: Comparison cell ─────────────────────────────────────────────────

function Cell({ value }: { value: string | boolean }) {
  if (value === true) {
    return (
      <td style={{ padding: "11px 16px", textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        <Check className="w-4 h-4 mx-auto" style={{ color: GREEN }} />
      </td>
    );
  }
  if (value === false) {
    return (
      <td style={{ padding: "11px 16px", textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        <X className="w-4 h-4 mx-auto" style={{ color: DIMMED, opacity: 0.5 }} />
      </td>
    );
  }
  return (
    <td style={{ padding: "11px 16px", textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 13, color: "#cbd5e1" }}>
      {value}
    </td>
  );
}

// ─── NavBar ──────────────────────────────────────────────────────────────────

function NavBar({ open, setOpen }: { open: boolean; setOpen: (v: boolean) => void }) {
  return (
    <nav style={{
      position: "sticky", top: 0, zIndex: 50,
      background: "rgba(5,7,10,0.94)", backdropFilter: "blur(20px)",
      borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "0 24px",
    }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link href="/">
          <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <img src={vexisLogo} alt="Vexis" style={{ height: 30, width: 30, borderRadius: 6, objectFit: "cover" }} />
            <span style={{ fontWeight: 700, fontSize: 17, color: "#f1f5f9", letterSpacing: "-0.02em" }}>Vexis</span>
          </div>
        </Link>
        <div className="hidden md:flex" style={{ gap: 32, alignItems: "center" }}>
          <Link href="/"><span style={{ color: MUTED, fontSize: 14, cursor: "pointer" }}>Home</span></Link>
          <span style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 600, borderBottom: `2px solid ${BLUE}`, paddingBottom: 2 }}>Pricing</span>
          <Link href="/login">
            <Button size="sm" style={{ background: BLUE, color: "#fff", border: "none", fontWeight: 600 }}>Sign In</Button>
          </Link>
        </div>
        <button onClick={() => setOpen(!open)} className="md:hidden"
          style={{ background: "none", border: "none", color: "#f1f5f9", cursor: "pointer", padding: 4 }}>
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div style={{ padding: "16px 0 20px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", gap: 16 }}>
          <Link href="/"><span style={{ color: MUTED, fontSize: 14, cursor: "pointer" }}>Home</span></Link>
          <Link href="/login">
            <Button size="sm" style={{ background: BLUE, color: "#fff", border: "none", width: "100%" }}>Sign In</Button>
          </Link>
        </div>
      )}
    </nav>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function PricingPage() {
  const [location] = useLocation();
  const [annual, setAnnual] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [recommendedId, setRecommendedId] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutErrorPlan, setCheckoutErrorPlan] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cp = params.get("currentPlan");
    const rec = params.get("recommended");
    const rsn = params.get("reason");
    if (cp) setCurrentPlanId(cp.toLowerCase());
    if (rec) setRecommendedId(rec.toLowerCase());
    else if (cp) {
      const idx = TIERS.findIndex(t => t.id === cp.toLowerCase());
      if (idx >= 0 && idx < TIERS.length - 1) setRecommendedId(TIERS[idx + 1].id);
    }
    if (rsn) setReason(rsn);

    // Auto-detect logged-in plan from session
    fetch('/api/billing/status', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.tier && !cp) {
          setCurrentPlanId(data.tier.toLowerCase());
        }
      })
      .catch(() => {});
  }, [location]);

  const handleCheckout = async (planKey: string) => {
    setCheckoutError(null);
    setCheckoutErrorPlan(null);
    setCheckoutLoading(planKey);
    try {
      // Fetch a fresh CSRF token (works for both authenticated and anonymous visitors)
      const csrfRes = await fetch("/api/csrf-token", { credentials: "include" });
      const { csrfToken } = await csrfRes.json();

      const billingCycle = annual ? "annual" : "monthly";
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify({ planKey, billingCycle }),
      });

      if (res.status === 401) {
        window.location.href = `/login?redirect=/pricing?recommended=${planKey}`;
        return;
      }

      if (res.status === 403) {
        setCheckoutError("Something went wrong — please refresh the page and try again.");
        setCheckoutErrorPlan(planKey);
        return;
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setCheckoutError(data.error || "Failed to start checkout — please try again.");
        setCheckoutErrorPlan(planKey);
      }
    } catch {
      setCheckoutError("Failed to start checkout — please try again.");
      setCheckoutErrorPlan(planKey);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const reasonLabels: Record<string, string> = {
    role_limit: "You've reached your active role limit — upgrade to open more roles.",
    candidate_limit: "You've hit your monthly candidate limit — upgrade to send more invites.",
    approaching_role_limit: "You're approaching your active role limit.",
    approaching_limit: "You're approaching your plan limits.",
    plan_upgrade: "Upgrade your plan to unlock more from Vexis.",
    feature_gate_canRemoveBranding: "Remove Vexis branding is available on Growth and above.",
    feature_gate_canEditTemplates: "Custom email templates are available from Growth and above.",
    feature_gate_whiteLabel: "White-label is available on Agency and above.",
    feature_gate_clientPortal: "The client portal is available on Agency and above.",
    role_expiry: "Your free-plan role is expiring — upgrade to keep it open.",
  };

  return (
    <div style={{ background: DARK_BG, minHeight: "100vh", color: "#f1f5f9", fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}>
      <NavBar open={mobileMenuOpen} setOpen={setMobileMenuOpen} />

      <main style={{ maxWidth: 1280, margin: "0 auto", padding: "0 20px 100px" }}>

        {/* Upgrade reason banner */}
        {reason && reasonLabels[reason] && (
          <div
            data-testid="upgrade-reason-banner"
            style={{
              margin: "28px auto 0", maxWidth: 640,
              background: "rgba(59,130,246,0.07)", border: "1px solid rgba(59,130,246,0.3)",
              borderRadius: 10, padding: "13px 20px",
              fontSize: 14, color: "#93c5fd", textAlign: "center", lineHeight: 1.5,
            }}
          >
            {reasonLabels[reason]}
          </div>
        )}

        {/* ── Hero ── */}
        <div style={{ textAlign: "center", padding: "60px 0 44px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: BLUE, textTransform: "uppercase", marginBottom: 16 }}>
            Simple, transparent pricing
          </p>
          <h1 style={{
            fontSize: "clamp(32px, 5.5vw, 56px)", fontWeight: 800,
            letterSpacing: "-0.035em", lineHeight: 1.08, marginBottom: 20, color: "#f8fafc",
          }}>
            Invest in better hires.
          </h1>
          <p style={{ fontSize: 16, color: MUTED, maxWidth: 560, margin: "0 auto 36px", lineHeight: 1.7 }}>
            Every plan includes the full Vexis assessment experience — structured video interviews, AI analysis, and human review.
            No hidden fees. No per-interview charges. Cancel anytime.
          </p>

          {/* Monthly / Annual toggle */}
          <div
            data-testid="billing-toggle"
            style={{
              display: "inline-flex", alignItems: "center",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 999, padding: "5px 6px", gap: 4,
            }}
          >
            <button
              data-testid="toggle-monthly"
              onClick={() => setAnnual(false)}
              style={{
                padding: "7px 22px", borderRadius: 999, border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: 600, transition: "all 0.18s",
                background: annual ? "transparent" : "#fff",
                color: annual ? MUTED : "#0a0a0a",
              }}
            >
              Monthly
            </button>
            <button
              data-testid="toggle-annual"
              onClick={() => setAnnual(true)}
              style={{
                padding: "7px 22px", borderRadius: 999, border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: 600, transition: "all 0.18s",
                display: "flex", alignItems: "center", gap: 8,
                background: annual ? "#fff" : "transparent",
                color: annual ? "#0a0a0a" : MUTED,
              }}
            >
              Annual
              <span style={{
                background: AMBER, color: "#000",
                fontSize: 10, fontWeight: 800,
                padding: "2px 8px", borderRadius: 999, letterSpacing: "0.03em",
              }}>
                −20%
              </span>
            </button>
          </div>

          {/* Currency note */}
          <p style={{ fontSize: 12, color: DIMMED, marginTop: 12 }}>
            Prices in NZD. GST not included.
          </p>
        </div>

        {/* ── Tier cards ── */}
        <div
          data-testid="pricing-tier-cards"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 16,
            alignItems: "stretch",
            marginBottom: 48,
          }}
          className="pricing-grid"
        >
          {TIERS.map(tier => {
            const isCurrent = currentPlanId === tier.id;
            const isRecommended = recommendedId === tier.id;
            const price = tier.priceMonthly === null
              ? null
              : annual ? tier.priceAnnual : tier.priceMonthly;

            const cardBorder = isCurrent
              ? GREEN
              : tier.elevated
              ? BLUE
              : "rgba(255,255,255,0.08)";

            const cardShadow = tier.elevated
              ? "0 0 0 2px #3B82F6, 0 20px 48px rgba(59,130,246,0.15)"
              : "none";

            const cardPadding = tier.elevated
              ? "30px 20px"
              : "22px 18px";

            return (
              <div
                key={tier.id}
                data-testid={`tier-card-${tier.id}`}
                style={{
                  border: `1px solid ${cardBorder}`,
                  borderRadius: 16,
                  padding: cardPadding,
                  background: tier.elevated
                    ? "rgba(59,130,246,0.06)"
                    : tier.enterprise
                    ? "rgba(255,255,255,0.01)"
                    : CARD_BG,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  boxShadow: isCurrent ? `0 0 0 2px ${GREEN}` : cardShadow,
                  position: "relative",
                  transition: "box-shadow 0.2s, border-color 0.2s",
                }}
              >
                {/* Status badges */}
                <div style={{ minHeight: 26, marginBottom: 14, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {isCurrent && (
                    <span
                      data-testid={`current-plan-badge-${tier.id}`}
                      style={{
                        background: GREEN, color: "#000",
                        fontSize: 10, fontWeight: 800,
                        padding: "3px 10px", borderRadius: 999, letterSpacing: "0.06em",
                      }}
                    >
                      CURRENT PLAN
                    </span>
                  )}
                  {isRecommended && !isCurrent && (
                    <span
                      data-testid={`recommended-badge-${tier.id}`}
                      style={{
                        background: BLUE, color: "#fff",
                        fontSize: 10, fontWeight: 800,
                        padding: "3px 10px", borderRadius: 999, letterSpacing: "0.06em",
                      }}
                    >
                      RECOMMENDED
                    </span>
                  )}
                  {tier.badgeLabel && !isCurrent && !isRecommended && (
                    <span style={{
                      background: tier.badgeColour || INDIGO, color: "#fff",
                      fontSize: 10, fontWeight: 800,
                      padding: "3px 10px", borderRadius: 999, letterSpacing: "0.04em",
                    }}>
                      {tier.badgeLabel}
                    </span>
                  )}
                </div>

                {/* Tier icon + name */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{
                    color: tier.elevated ? BLUE : tier.enterprise ? PURPLE : INDIGO,
                    opacity: 0.85,
                  }}>
                    {tier.icon}
                  </div>
                  <h2 style={{
                    fontWeight: 700,
                    fontSize: tier.elevated ? 20 : 17,
                    margin: 0, color: "#f1f5f9",
                  }}>
                    {tier.displayName}
                  </h2>
                </div>

                {/* Subtitle */}
                <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.5, marginBottom: 18 }}>
                  {tier.subtitle}
                </p>

                {/* Price block */}
                <div style={{ marginBottom: 20 }}>
                  {tier.priceMonthly === null ? (
                    <div>
                      <div style={{ fontSize: 30, fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" }}>
                        Custom
                      </div>
                      <p style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>For large agencies and enterprise talent teams</p>
                    </div>
                  ) : tier.priceMonthly === 0 ? (
                    <div>
                      <div style={{ fontSize: 34, fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" }}>
                        $0
                        <span style={{ fontSize: 14, fontWeight: 400, color: MUTED }}> NZD / mo</span>
                      </div>
                      <p style={{ fontSize: 12, color: DIMMED, marginTop: 4 }}>{tier.billingNote}</p>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 4, flexWrap: "wrap" }}>
                        <span style={{ fontSize: tier.elevated ? 34 : 28, fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" }}>
                          ${price}
                        </span>
                        <span style={{ fontSize: 13, color: MUTED }}>NZD / mo</span>
                      </div>
                      {annual && tier.priceAnnual && tier.priceMonthly && (
                        <p style={{ fontSize: 11, color: AMBER, marginTop: 3 }}>
                          Billed ${tier.priceAnnual * 12} NZD/yr — save ${(tier.priceMonthly - tier.priceAnnual) * 12} NZD
                        </p>
                      )}
                      {!annual && (
                        <p style={{ fontSize: 11, color: DIMMED, marginTop: 3 }}>{tier.billingNote}</p>
                      )}

                    </div>
                  )}
                </div>

                {/* Feature list */}
                <ul style={{
                  listStyle: "none", padding: 0, margin: "0 0 16px",
                  display: "flex", flexDirection: "column", gap: 8, flex: 1,
                }}>
                  {tier.features.map((f, i) => (
                    <li key={i} style={{
                      display: "flex", alignItems: "flex-start", gap: 8,
                      fontSize: 12.5, color: "#cbd5e1", lineHeight: 1.4,
                    }}>
                      <Check
                        className="flex-shrink-0"
                        style={{ width: 14, height: 14, color: tier.elevated ? BLUE : GREEN, marginTop: 1 }}
                      />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Free-tier restriction note */}
                {tier.restrictionNote && (
                  <p style={{
                    fontSize: 11, color: AMBER, lineHeight: 1.5,
                    padding: "8px 10px", borderRadius: 8,
                    background: "rgba(245,158,11,0.08)",
                    border: "1px solid rgba(245,158,11,0.2)",
                    marginBottom: 16,
                  }}>
                    {tier.restrictionNote}
                  </p>
                )}

                {/* CTA button */}
                {isCurrent ? (
                  <>
                    <button
                      data-testid={`cta-current-${tier.id}`}
                      disabled
                      style={{
                        ...ctaButtonStyle(tier.ctaStyle, true),
                        padding: "10px 16px", borderRadius: 8, cursor: "default",
                      }}
                    >
                      Current Plan
                    </button>
                    <a
                      href="/settings/billing"
                      data-testid={`manage-billing-${tier.id}`}
                      style={{
                        display: "block", textAlign: "center", marginTop: 8,
                        fontSize: 12, color: "#3b82f6", textDecoration: "underline",
                      }}
                    >
                      Manage Billing →
                    </a>
                  </>
                ) : tier.ctaHref ? (
                  <a href={tier.ctaHref} style={{ textDecoration: "none" }}>
                    <button
                      data-testid={`cta-${tier.id}`}
                      style={{
                        ...ctaButtonStyle(tier.ctaStyle, false),
                        padding: "10px 16px", borderRadius: 8, cursor: "pointer",
                      }}
                    >
                      {tier.cta}
                    </button>
                  </a>
                ) : (
                  <>
                    <button
                      data-testid={`cta-${tier.id}`}
                      onClick={() => tier.planKey && handleCheckout(tier.planKey)}
                      disabled={checkoutLoading === tier.planKey}
                      style={{
                        ...ctaButtonStyle(tier.ctaStyle, false),
                        padding: "10px 16px", borderRadius: 8,
                        cursor: checkoutLoading === tier.planKey ? "not-allowed" : "pointer",
                        opacity: checkoutLoading === tier.planKey ? 0.7 : 1,
                      }}
                    >
                      {checkoutLoading === tier.planKey ? "Redirecting…" : tier.cta}
                    </button>
                    {checkoutErrorPlan === tier.planKey && checkoutError && (
                      <p data-testid={`checkout-error-${tier.id}`} style={{
                        marginTop: 8, fontSize: 12, color: "#dc2626",
                        textAlign: "center", lineHeight: 1.4,
                      }}>
                        {checkoutError}
                      </p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Responsive grid style */}
        <style>{`
          @media (max-width: 1100px) {
            .pricing-grid {
              grid-template-columns: repeat(3, 1fr) !important;
            }
          }
          @media (max-width: 700px) {
            .pricing-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>

        {/* ── Comparison table ── */}
        <div style={{ marginBottom: 80 }}>
          <h2 style={{
            textAlign: "center", fontWeight: 700,
            fontSize: "clamp(20px, 3vw, 28px)",
            marginBottom: 32, color: "#f1f5f9", letterSpacing: "-0.02em",
          }}>
            Compare all features
          </h2>

          <div
            data-testid="comparison-table"
            style={{
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 14, overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                    <th style={{
                      textAlign: "left", padding: "14px 20px",
                      fontSize: 12, color: MUTED, fontWeight: 600,
                      borderBottom: "1px solid rgba(255,255,255,0.07)",
                      position: "sticky", left: 0,
                      background: "rgba(10,10,14,0.98)",
                      minWidth: 180,
                    }}>
                      Feature
                    </th>
                    {TIERS.map(t => (
                      <th key={t.id} style={{
                        padding: "14px 16px", textAlign: "center",
                        fontSize: 13, fontWeight: 700,
                        color: t.elevated ? BLUE : t.enterprise ? PURPLE : "#f1f5f9",
                        borderBottom: "1px solid rgba(255,255,255,0.07)",
                        minWidth: 110,
                      }}>
                        {t.displayName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr
                      key={i}
                      style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)" }}
                    >
                      <td style={{
                        padding: "11px 20px",
                        fontSize: 13, color: "#cbd5e1",
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        position: "sticky", left: 0,
                        background: i % 2 === 0 ? "rgba(5,7,10,0.98)" : "rgba(12,14,19,0.98)",
                        fontWeight: 500,
                      }}>
                        {row.label}
                      </td>
                      <Cell value={row.free} />
                      <Cell value={row.starter} />
                      <Cell value={row.growth} />
                      <Cell value={row.agency} />
                      <Cell value={row.enterprise} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Trust strip ── */}
        <div style={{
          display: "flex", flexWrap: "wrap", justifyContent: "center",
          gap: "12px 32px", marginBottom: 64,
        }}>
          {[
            { icon: <Lock className="w-4 h-4" />, text: "Bank-grade encryption" },
            { icon: <span style={{ fontSize: 14 }}>🇳🇿</span>, text: "NZ Privacy Act 2020 compliant" },
            { icon: <Check className="w-4 h-4" />, text: "Human review on every assessment" },
            { icon: <RefreshCw className="w-4 h-4" />, text: "Cancel anytime" },
            { icon: <Phone className="w-4 h-4" />, text: "Local support team" },
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 8,
              fontSize: 13, color: MUTED,
            }}>
              <span style={{ color: BLUE }}>{item.icon}</span>
              {item.text}
            </div>
          ))}
        </div>

        {/* ── FAQ ── */}
        <div style={{ maxWidth: 700, margin: "0 auto 80px" }}>
          <h2 style={{
            textAlign: "center", fontWeight: 700,
            fontSize: "clamp(20px, 3vw, 28px)",
            marginBottom: 32, color: "#f1f5f9", letterSpacing: "-0.02em",
          }}>
            Frequently asked questions
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {FAQS.map((faq, i) => (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                style={{
                  border: openFaq === i
                    ? "1px solid rgba(59,130,246,0.4)"
                    : "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 12, overflow: "hidden",
                  transition: "border-color 0.2s",
                }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    width: "100%",
                    background: openFaq === i ? "rgba(59,130,246,0.06)" : "rgba(255,255,255,0.02)",
                    border: "none", cursor: "pointer",
                    padding: "18px 22px",
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16,
                    textAlign: "left",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 14, color: "#f1f5f9", lineHeight: 1.4 }}>
                    {faq.q}
                  </span>
                  {openFaq === i
                    ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: BLUE }} />
                    : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: MUTED }} />
                  }
                </button>
                {openFaq === i && (
                  <div style={{
                    padding: "0 22px 18px",
                    fontSize: 14, color: MUTED, lineHeight: 1.7,
                  }}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Enterprise CTA band ── */}
        <div style={{
          border: "1px solid rgba(139,92,246,0.25)",
          borderRadius: 16, padding: "44px 40px",
          background: "rgba(139,92,246,0.04)",
          textAlign: "center", maxWidth: 720, margin: "0 auto",
        }}>
          <h3 style={{
            fontWeight: 800, fontSize: "clamp(20px, 3vw, 28px)",
            marginBottom: 12, letterSpacing: "-0.02em",
          }}>
            Need something custom?
          </h3>
          <p style={{
            fontSize: 15, color: MUTED,
            maxWidth: 480, margin: "0 auto 28px", lineHeight: 1.65,
          }}>
            Enterprise contracts include dedicated infrastructure, custom SLA, SSO,
            unlimited seats, and hands-on executive onboarding. We work directly with
            your IT and legal teams.
          </p>
          <a href="mailto:partnerships@vexistalentagency.com" style={{ textDecoration: "none" }}>
            <Button
              data-testid="enterprise-cta"
              style={{
                background: PURPLE, color: "#fff", border: "none",
                fontSize: 14, fontWeight: 700,
                padding: "12px 32px", height: "auto", borderRadius: 8,
              }}
            >
              Talk to Us
            </Button>
          </a>
        </div>

        {/* Footer note */}
        <p style={{
          textAlign: "center", fontSize: 12, color: DIMMED,
          marginTop: 52, lineHeight: 1.6,
        }}>
          All prices in NZD. GST not included. Vexis is a registered New Zealand business.
          <br />AUD pricing available at checkout. Annual plans billed in full at the start of each billing year.
        </p>
      </main>
    </div>
  );
}
